import { TabList, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MobileStepper,
  Paper,
  Tab,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export const StyledMobileStepper = styled(MobileStepper)(({ theme }) => ({
  width: '100%',
  background: 'transparent',
  padding: 0,
  '& .MuiMobileStepper-progress': {
    width: '100%',
    height: 14,
    borderRadius: '60px',
    backgroundColor: '#FAFCFF',
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '60px',
    },
  },
}));

export const SummaryMobileStepper = styled(MobileStepper)(({ theme }) => ({
  width: '100%',
  background: 'transparent',
  padding: 0,
  '& .MuiMobileStepper-progress': {
    width: '100%',
    height: 14,
    borderRadius: '60px',
    backgroundColor: '#E5E7EB',
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '60px',
    },
  },
}));

export const OutlinedButton = styled(Button)(({ theme }) => ({
  width: '220px',
  fontWeight: '600',
  borderRadius: '10px',
  display: 'flex',
  gap: '8px',
  color: theme.palette.primary.dark,
  backgroundColor: 'transparent',
  border: `1.5px solid ${theme.palette.primary.dark}`,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    color: theme.palette.primary.dark,
  },
  '&:active': {
    backgroundColor: theme.palette.primary.dark,
  },
  span: {
    fontSize: '20px',
  },
}));

export const StyledPaper = styled(Paper)(() => ({
  padding: '16px',
  borderRadius: '16px',
  backgroundColor: '#F5F5F4',
  boxShadow: 'none',
  border: '1px solid',
  borderColor: '#ADADAD8A',
}));

export const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '16px 8px',
}));

export const OSNumber = styled(Typography)(({ theme }) => ({
  width: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.dark,
  borderRadius: '10px',
  color: theme.palette.common.white,
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  padding: '26px 40px',
  borderRadius: '20px 20px 0 0',
  color: theme.palette.primary.dark,
  textTransform: 'none',
  fontSize: '18px',
  fontWeight: '600',
  '&.Mui-selected': {
    backgroundColor: '#F5F5F4',
    boxShadow: '0px 1px 2px #61335429',
  },
  [theme.breakpoints.down('xl')]: {
    padding: '20px',
  },
}));

export const StyledTabPanel = styled(TabPanel)(() => ({
  backgroundColor: '#F5F5F4',
  boxShadow: '0px 1px 2px #61335429',
  minHeight: '600px',
  padding: '32px',
}));

export const StyledTabList = styled(TabList)(({ theme }) => ({
  '& .MuiTabs-scrollButtons': {
    color: theme.palette.primary.main,
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    alignSelf: 'center',
    margin: '0 8px',
    '& svg': {
      fontSize: '28px',
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    '&:active': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: alpha(theme.palette.primary.main, 0.2),
  margin: '8px 0',
}));

export const GridSelect = styled(Grid)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoFlow: 'column',
  gridTemplateRows: 'repeat(4, auto)',
}));

export const GridSelectLong = styled(Grid)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoFlow: 'column',
  gridTemplateRows: 'repeat(5, auto)',
}));

export const GridSelectEquip = styled(Grid)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoFlow: 'column',
  gridTemplateRows: 'repeat(7, auto)',
}));

export const GridSelectUses = styled(Grid)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridAutoFlow: 'column',
  gridTemplateRows: 'repeat(5, auto)',
}));

export const AnswerTag = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  padding: '4px 12px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  borderRadius: '90px',
}));

export const AnswerTagsBox = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

export const SectionIndexGroupList = styled(Box)(() => ({
  padding: 0,
}));

export const SectionIndexGroupLabel = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  color: theme.palette.primary.dark,
  lineHeight: '32px',
}));

export const SectionIndexOptionLabel = styled(Box)(() => ({
  fontWeight: 500,
}));

export const SectionIndexOptionSubLabel = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

export const StyledSectionIndexAutocomplete = styled(Autocomplete)(
  ({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: 400,
    alignSelf: 'flex-end',
    '& .MuiInputBase-root': {
      borderRadius: '30px',
    },
    '& .MuiButtonBase-root': {
      marginLeft: '4px',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      fontSize: '1.5rem',
    },
  })
) as typeof Autocomplete;

export const StyledSectionIndexPaper = styled(Paper)(({ theme }) => ({
  '& .MuiAutocomplete-option': {
    color: theme.palette.common.black,
  },
  '& .MuiAutocomplete-option:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
  },
  '& .MuiAutocomplete-option.Mui-focused': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
  },
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    color: theme.palette.primary.main,
  },
  '& .MuiAutocomplete-groupLabel': {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },
}));

export const StyledContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 20,
  gap: 5,
}));

export const StyledChip = styled(Chip)(() => ({
  fontWeight: 600,
  fontSize: '16px',
  padding: '16px 20px',
}));
